import { Badge } from "@devhop/ui";
import { useMemo } from "react";
import type { AssignmentDTO, EventShowDTO } from "../../domain/contracts";

type EventPerformerCostReportProps = {
  assignments: AssignmentDTO[];
  eventShows: EventShowDTO[];
  totalEventShowCount: number;
};

function formatMoney(value: number) {
  return `${new Intl.NumberFormat("lo-LA").format(value)} ກີບ`;
}

function performerShowCount(
  rows: AssignmentDTO[],
  totalEventShowCount: number,
): number {
  const ids = new Set<string>();
  for (const row of rows) {
    for (const id of row.eventShowIds ?? []) {
      ids.add(id);
    }
  }
  if (ids.size > 0) return ids.size;
  const maxLine = Math.max(...rows.map((row) => row.showCount), 0);
  return maxLine > 0 ? maxLine : totalEventShowCount;
}

function performerHasPartial(
  rows: AssignmentDTO[],
  totalEventShowCount: number,
): boolean {
  if (totalEventShowCount < 2) return false;
  return rows.some((row) => {
    const selected =
      row.eventShowIds && row.eventShowIds.length > 0
        ? row.eventShowIds.length
        : row.showCount;
    return selected > 0 && selected < totalEventShowCount;
  });
}

export function EventPerformerCostReport({
  assignments,
  eventShows,
  totalEventShowCount,
}: EventPerformerCostReportProps) {
  const rows = useMemo(() => {
    const map = new Map<string, AssignmentDTO[]>();
    for (const assignment of assignments) {
      const list = map.get(assignment.performerId) ?? [];
      list.push(assignment);
      map.set(assignment.performerId, list);
    }

    return [...map.entries()]
      .map(([performerId, performerRows]) => ({
        performerId,
        performerName: performerRows[0]?.performerName ?? performerId,
        lineCount: performerRows.length,
        showCount: performerShowCount(performerRows, totalEventShowCount),
        grossSubtotal: performerRows.reduce(
          (sum, row) => sum + row.grossAmount,
          0,
        ),
        partial: performerHasPartial(performerRows, totalEventShowCount),
      }))
      .sort((a, b) => a.performerName.localeCompare(b.performerName));
  }, [assignments, totalEventShowCount]);

  const totalGross = rows.reduce((sum, row) => sum + row.grossSubtotal, 0);

  if (rows.length === 0) {
    return (
      <section className="rounded-xl border bg-card px-5 py-8 text-center">
        <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີບັນທັດຄ່າຈ້າງ</p>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-xl border bg-card">
      <div className="border-b px-5 py-4">
        <p className="font-semibold text-muted-foreground text-xs uppercase tracking-widest">
          ຄ່າຈ້າງຕາມຄົນ
        </p>
        <p className="mt-1 text-muted-foreground text-xs">
          {rows.length} ຄົນ · {assignments.length} ບັນທັດ ·{" "}
          {eventShows.length > 0 ? `${eventShows.length} ໂຊ` : "—"} · ລວມ{" "}
          {formatMoney(totalGross)}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[32rem] text-sm">
          <thead>
            <tr className="border-b bg-muted/30 text-left text-muted-foreground text-xs">
              <th className="px-5 py-2.5 font-medium">ນັກສະແດງ</th>
              <th className="px-3 py-2.5 font-medium tabular-nums">ບັນທັດ</th>
              <th className="px-3 py-2.5 font-medium tabular-nums">ໂຊ</th>
              <th className="px-3 py-2.5 font-medium tabular-nums">Gross</th>
              <th className="px-5 py-2.5 font-medium">ໝາຍເຫດ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.performerId} className="border-b last:border-b-0">
                <td className="px-5 py-3 font-medium">{row.performerName}</td>
                <td className="px-3 py-3 tabular-nums">{row.lineCount}</td>
                <td className="px-3 py-3 tabular-nums">
                  {totalEventShowCount >= 2
                    ? `${row.showCount}/${totalEventShowCount}`
                    : row.showCount || "—"}
                </td>
                <td className="px-3 py-3 font-medium tabular-nums">
                  {formatMoney(row.grossSubtotal)}
                </td>
                <td className="px-5 py-3">
                  {row.partial ? (
                    <Badge
                      variant="outline"
                      className="border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200"
                    >
                      ບາງໂຊ
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
